import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { toast } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import {
  CREATE_CARD_EPHEMERAL_KEY,
  GET_CARD_EPHEMERAL_KEY,
} from "../graphql/card";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  getCardCopyMessage,
  getCardFieldLabels,
  getCardIssuerClient,
  toCardToken,
  type CardElementOptions,
  type CardIssuerFeatures,
  type LegacyCardKey,
} from "utils/cards/cardViewer";

const useIssuingElement = () => {
  const { t } = useTranslation();

  const [createCardEphemeralKey, { loading: createLoading }] = useMutation<{
    createCardEphemeralKey: { token: string };
  }>(CREATE_CARD_EPHEMERAL_KEY, {
    onError: (error) => {
      toast.error(error?.message || t("Please try again!"));
    },
  });

  const [getCardEphemeralKey, { loading: getLoading, error }] = useLazyQuery<{
    getCardEphemeralKey: { data: LegacyCardKey };
  }>(GET_CARD_EPHEMERAL_KEY, {
    fetchPolicy: "network-only",
  });

  const [currentCardId, setCurrentCardId] = useState("");
  const [isCardDataDisplayed, setIsCardDataDisplayed] = useState(false);
  const [revealedIssuer, setRevealedIssuer] = useState<{
    cardId: string;
    features: CardIssuerFeatures;
  }>();
  // Each reveal takes a number; one whose number is no longer current belongs to a card the user left.
  const revealRef = useRef(0);

  // A reveal still in flight when the card UI unmounts stops at its next await.
  useEffect(() => {
    const reveals = revealRef;
    return () => {
      reveals.current += 1;
    };
  }, []);

  const resetCardData = () => {
    revealRef.current += 1;
    setIsCardDataDisplayed(false);
    setCurrentCardId("");
    setRevealedIssuer(undefined);
  };

  /** The features of the issuer drawing a card's details, only for the card whose reveal chose it. */
  const getIssuerFeatures = (cardId?: string | null) =>
    revealedIssuer && revealedIssuer.cardId === cardId
      ? revealedIssuer.features
      : undefined;

  /** Resolves true once the card shows, false when its reveal failed or was left. */
  async function handleViewCard(
    cardId: string,
    style: CardElementOptions,
  ): Promise<boolean> {
    if (cardId === currentCardId || isCardDataDisplayed) return true;
    const reveal = ++revealRef.current;
    const isCurrent = () => reveal === revealRef.current;
    setCurrentCardId(cardId);

    try {
      const { data: created } = await createCardEphemeralKey({
        variables: { id: cardId },
      });
      const token = created?.createCardEphemeralKey.token;
      if (!token || !isCurrent()) return false;

      const { data } = await getCardEphemeralKey({ variables: { token } });
      if (!isCurrent()) return false;
      const cardKey = data?.getCardEphemeralKey.data;
      if (!cardKey) throw new Error("The card key did not arrive.");

      const cardToken = toCardToken(cardKey);
      const client = getCardIssuerClient(cardToken);
      // The issuer's slots have to be on the page before it mounts into them.
      flushSync(() => setRevealedIssuer({ cardId, features: client.features }));
      const shown = await client.render({
        cardToken,
        selectors: {
          cardNumber: `#card-number-${cardId}`,
          cvv: `#card-cvc-${cardId}`,
          expirationDate: `#card-exp-${cardId}`,
          copyCardNumber: `#card-number-copy-${cardId}`,
        },
        style: style.style,
        onCopy: (field) => toast.success(getCardCopyMessage(field, t)),
        onLateError: () =>
          toast.error(t("SomethingWentWrongDescriptionWithSupport")),
        isCurrent,
        labels: getCardFieldLabels(t),
      });
      if (!shown || !isCurrent()) return false;
      setIsCardDataDisplayed(true);
      return true;
    } catch (error) {
      if (isCurrent()) {
        setRevealedIssuer(undefined);
        console.error(error);
        toast.error(t("SomethingWentWrongDescriptionWithSupport"));
      }
      return false;
    } finally {
      if (isCurrent()) setCurrentCardId("");
    }
  }

  return {
    handleViewCard,
    getIssuerFeatures,
    loading: createLoading || getLoading,
    error,
    resetCardData,
  };
};

export default useIssuingElement;
